import { motion } from 'framer-motion';
import { Calendar, MapPin, Plus } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import { getAllSites } from '../SiteManagement/siteApi';
import { Card } from '../ui';
import { wasteLoggingApi } from './wasteLoggingApi';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Normalizes category from API format to UI format
 */
const normalizeCategoryForDisplay = (category) => {
  if (!category) return 'Production waste';
  const normalized = category.toLowerCase();
  if (normalized === 'prod_waste') return 'Production waste';
  if (normalized === 'serv_waste') return 'Service waste';
  if (normalized === 'exp_waste') return 'Expiry waste';
  return category;
};

/**
 * Maps API waste log to UI format
 * Handles all fields from the API response according to the documentation
 */
const mapWasteLogToUI = (log) => {
  return {
    id: log.waste_id || log.id,
    logDate: log.waste_date || log.logDate || log.date,
    siteId: log.site_id || log.siteId,
    siteName: log.site_name || log.siteName,
    item: log.product_name || log.item || log.product_id || 'Unknown',
    quantity: parseFloat(log.quantity || 0),
    reason: log.reason || 'Not specified',
    itemCost: parseFloat(log.cost_per_unit || 0),
    category: normalizeCategoryForDisplay(log.category),
    estimatedLoss: parseFloat(log.value_of_loss || log.estimatedLoss || 0),
    // Additional fields from API
    isStockItem: log.is_stock_item !== undefined ? log.is_stock_item : true,
    stockItemId: log.stock_item_id || null,
    productId: log.product_id || null,
    notes: log.notes || '',
    imageKey: log.image_key || null,
    imageContentType: log.image_content_type || null,
    metadata: log.metadata || {},
    createdAt: log.created_at || null,
    createdBy: log.created_by || null,
    tenantId: log.tenant_id || null,
  };
};

const WasteLoggingView = () => {
  // Log screen view
  useScreenLogging('WasteLoggingView', {
    component: 'WasteLoggingView',
    screenType: 'waste-logging',
  });

  const navigate = useNavigate();
  const { openOverlay } = useOverlay();
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState('');
  
  const [wasteLogs, setWasteLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch sites on component mount
  useEffect(() => {
    let isMounted = true;

    const fetchSites = async () => {
      setIsSitesLoading(true);
      setSitesError('');
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
            ? response
            : [];
        const sorted = [...list].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        if (isMounted) {
          setSites(sorted);
        }
      } catch (error) {
        if (isMounted) {
          setSitesError(
            error?.message || 'Unable to load sites. Please try again later.'
          );
        }
      } finally {
        if (isMounted) {
          setIsSitesLoading(false);
        }
      }
    };

    fetchSites();

    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch waste logs when site or month changes
  useEffect(() => {
    if (!selectedSite) {
      setWasteLogs([]);
      return;
    }

    let isMounted = true;

    const fetchWasteLogs = async () => {
      setIsLoading(true);
      setError('');
      
      try {
        // Calculate date range for the selected month
        const [year, month] = selectedMonth.split('-');
        const startDate = `${year}-${month}-01`;
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;

        const siteId = selectedSite.site_id || selectedSite.id || selectedSite.siteId;
        
        const result = await wasteLoggingApi.getWasteLogs({
          site_id: siteId,
          start_date: startDate,
          end_date: endDate,
          limit: 1000, // Get all logs for the month
        });

        if (result.success) {
          const mappedLogs = (result.wasteLogs || []).map(mapWasteLogToUI);
          if (isMounted) {
            setWasteLogs(mappedLogs);
          }
        } else {
          if (isMounted) {
            // Show specific message for CORS errors
            if (result.isCorsError) {
              setError('CORS Error: Unable to connect to the API. This may be a preflight (OPTIONS) request issue. Please check with the backend team to ensure CORS is properly configured for the GET /stock/wastage endpoint.');
            } else {
              setError(result.error || 'Failed to fetch waste logs');
            }
            setWasteLogs([]);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to fetch waste logs');
          setWasteLogs([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchWasteLogs();

    return () => {
      isMounted = false;
    };
  }, [selectedSite, selectedMonth]);

  // Filter waste logs by site and month (client-side filtering as backup)
  const filteredWasteLogs = useMemo(() => {
    if (!selectedSite) return [];
    return wasteLogs.filter((log) => {
      const matchesSite = (selectedSite.site_id || selectedSite.id || selectedSite.siteId) === log.siteId;
      
      // Filter by month based on logDate (additional client-side filtering)
      const logDate = new Date(log.logDate);
      const [year, month] = selectedMonth.split('-');
      const matchesMonth =
        logDate.getFullYear() === parseInt(year) &&
        logDate.getMonth() + 1 === parseInt(month);
      
      return matchesSite && matchesMonth;
    });
  }, [wasteLogs, selectedSite, selectedMonth]);

  // Group filtered logs by date
  const groupedByDate = useMemo(() => {
    const grouped = {};
    filteredWasteLogs.forEach((log) => {
      const date = log.logDate;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(log);
    });
    return grouped;
  }, [filteredWasteLogs]);

  // Get dates sorted
  const dates = useMemo(() => {
    return Object.keys(groupedByDate).sort((a, b) => new Date(b) - new Date(a));
  }, [groupedByDate]);

  // Calculate totals for a date
  const getDateTotals = (date) => {
    const items = groupedByDate[date] || [];
    const totalItems = items.length;
    const totalLoss = items.reduce((sum, item) => {
      return sum + (item.estimatedLoss || 0);
    }, 0);
    return { totalItems, totalLoss };
  };


  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Header with Title and Create Button */}
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className={typography.h2} style={{ color: '#000000' }}>
                Waste Logging
              </h1>
              <p className="mt-2 text-gray-600">
                View and manage waste logs
              </p>
            </div>
            <button
              onClick={() => navigate('/onboarding-waste-logging/create')}
              className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
            >
              <Plus className="w-4 h-4" />
              Create Waste Logging
            </button>
          </div>

          <div className="rounded-3xl">
            {/* Site and Month Selection */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
              <div className="min-w-[200px]">
                <label htmlFor="waste-logging-site" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                  <MapPin className="w-4 h-4" />
                  Select Site
                </label>
                <select
                  id="waste-logging-site"
                  value={selectedSite ? (selectedSite.site_id || selectedSite.id) : ''}
                  onChange={(e) => {
                    const siteId = e.target.value;
                    const site = sites.find(s => 
                      (s.site_id || s.id || s.siteId) === siteId
                    );
                    setSelectedSite(site || null);
                  }}
                  className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  required
                  disabled={isSitesLoading}
                >
                  <option value="">
                    {isSitesLoading ? 'Loading sites...' : 'Select a site'}
                  </option>
                  {sites.map((site) => (
                    <option 
                      key={site.site_id || site.id || site.siteId} 
                      value={site.site_id || site.id || site.siteId}
                    >
                      {site.site_name || site.name || 'Unknown Site'}
                    </option>
                  ))}
                </select>
                {sitesError && (
                  <p className="text-sm text-red-600 mt-1">{sitesError}</p>
                )}
              </div>

              <div className="min-w-[200px]">
                <label htmlFor="waste-logging-month" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                  <Calendar className="w-4 h-4" />
                  Select Month
                </label>
                <input
                  id="waste-logging-month"
                  type="month"
                  value={selectedMonth}
                  onChange={(event) => setSelectedMonth(event.target.value)}
                  className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                />
              </div>
            </div>

            {/* Waste Logs Cards - Grouped by Date */}
            <div className="space-y-4">
              {isLoading ? (
                <Card.Light className="p-8 text-center">
                  <p className="text-gray-500 text-lg">Loading waste logs...</p>
                </Card.Light>
              ) : error ? (
                <Card.Light className="p-8 text-center">
                  <p className="text-red-600 text-lg">{error}</p>
                </Card.Light>
              ) : dates.length > 0 ? (
                <>
                  {dates.map((date, dateIndex) => {
                    const { totalItems, totalLoss } = getDateTotals(date);
                    const itemsForDate = groupedByDate[date] || [];
                    
                    return (
                      <motion.div
                        key={date}
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <Card.Light
                          className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
                          onClick={() => {
                            // Show detail modal with all items for this date using OverlayContext
                            openOverlay('wasteLoggingDetails', {
                              date: date,
                              groupedByDate: groupedByDate,
                              getDateTotals: getDateTotals,
                              navigate: navigate
                            });
                          }}
                        >
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold">
                                  {dateIndex + 1}
                                </div>
                                <h3 className="text-xl font-semibold text-gray-800">
                                  {new Date(date).toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                  })}
                                </h3>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-2 gap-3 text-sm">
                                <div>
                                  <span className="text-gray-600">Total Items: </span>
                                  <span className="font-medium">{totalItems}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Total Loss: </span>
                                  <span className="font-medium text-red-600">
                                    ₹{totalLoss.toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card.Light>
                      </motion.div>
                    );
                  })}
                </>
              ) : (
                <Card.Light className="p-8 text-center">
                  <p className="text-gray-500 text-lg">
                    {!selectedSite
                      ? 'Please select a site to view waste logs.'
                      : 'No waste logs found for the selected filters.'}
                  </p>
                </Card.Light>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WasteLoggingView;

